/*
  BuildDirect — Flooring Experts page conversion events

  WHY A SECOND EVENT SCRIPT EXISTS

  assets/lp-sample-picker.js already pushes the campaign event set, including
  `lp_expert_cta` for anything carrying `data-lp-expert-cta`. It returns early
  unless the page has a `[data-lp-page]` element, which only sections/lp-hero
  emits, and lp-hero also stamps a campaign theme id onto every event it sends.
  /pages/flooring-experts is evergreen, not a campaign, so borrowing that
  attribution would put a fake campaign on real traffic.

  So this file does the same job for one page and stays out of everything else:
  no `[data-bd-expert-page]` element, no listeners, no events. That marker is
  emitted by sections/bd-expert-hero.liquid.

  WHY THE CTAs ARE CLASSIFIED BY HREF, NOT TAGGED IN THE MARKUP

  Most of the buttons on this page belong to shared sections — lp-expert,
  lp-steps, bd-final-cta, lp-sticky-cta — which also serve six campaign landing
  pages. Adding attributes there to instrument one page would edit files that
  have nothing to do with it. An href already says what a link does: tel: is a
  call, mailto: is an email, the flooring collection is the sample CTA. Reading
  intent off the href keeps the instrumentation in one file.

  Location comes from the nearest `data-expert-zone`, else the enclosing
  `.shopify-section` id with Shopify's prefix stripped, which is the section key
  from templates/page.flooring-experts.json — "expert", "steps", "faq", "final".
  That means the report reads in the same words as the template.

  EVENTS

    expert_page_view    once, on load
    expert_cta_click    a sample, call, email or contact link was clicked
    expert_roster_view  the roster reached the viewport (once)
    expert_faq_open     an FAQ was expanded (not collapsed)

  Every event carries `page_id` from the hero setting so a future second expert
  page reports separately without a code change.
*/
(function () {
  'use strict';

  var root = document.querySelector('[data-bd-expert-page]');
  if (!root) return;

  // The theme editor re-renders sections in place, which can run this file
  // again on a page that is already instrumented. One flag on the element is
  // cheaper than removing and re-adding listeners.
  if (root.getAttribute('data-bd-expert-bound') === '1') return;
  root.setAttribute('data-bd-expert-bound', '1');

  var pageId = root.getAttribute('data-bd-expert-page') || 'flooring-experts';

  function push(event, payload) {
    window.dataLayer = window.dataLayer || [];
    var data = { event: event, page_id: pageId };
    if (payload) {
      for (var key in payload) {
        if (Object.prototype.hasOwnProperty.call(payload, key)) data[key] = payload[key];
      }
    }
    window.dataLayer.push(data);
  }

  /* ------------------------------------------------------------- location */

  function zoneOf(el) {
    var tagged = el.closest('[data-expert-zone]');
    if (tagged) return tagged.getAttribute('data-expert-zone') || 'unknown';

    var section = el.closest('.shopify-section');
    if (section && section.id) {
      // shopify-section-template--123__faq → faq
      var id = section.id.replace(/^shopify-section-/, '');
      var split = id.lastIndexOf('__');
      return split === -1 ? id : id.slice(split + 2);
    }

    return 'unknown';
  }

  /* ----------------------------------------------------------- CTA clicks */

  /*
    The sample CTA's destination is the one thing here that moves. It pointed at
    /pages/flooring-samples until August 2026 and now points at
    /collections/flooring, because you pick the samples off the products. Both
    are recognised: the old path may still be linked from elsewhere, and a CTA
    that stops reporting is worse than one extra pattern.

    The collection pattern is anchored on a boundary on purpose. A bare
    /collections/ prefix would file every category link on the page as a sample
    CTA; /collections/flooring-accessories is not this button.
  */
  function ctaType(href) {
    if (/^tel:/i.test(href)) return 'call';
    if (/^mailto:/i.test(href)) return 'email';
    if (/\/collections\/flooring(?:[/?#]|$)/i.test(href)) return 'samples';
    if (/\/pages\/flooring-samples/i.test(href)) return 'samples';
    if (/\/pages\/contact/i.test(href)) return 'contact';
    return null;
  }

  document.addEventListener(
    'click',
    function (event) {
      var target = event.target;
      if (!target || !target.closest) return;

      var link = target.closest('a[href]');
      if (!link) return;

      var href = link.getAttribute('href') || '';
      var type = ctaType(href);
      if (!type) return;

      push('expert_cta_click', {
        cta_type: type,
        cta_location: zoneOf(link),
        // Trimmed: a button label is a few words, and an accidental block of
        // copy in a link should not become an unbounded event property.
        cta_label: (link.textContent || '').replace(/\s+/g, ' ').trim().slice(0, 80),
        cta_href: href
      });
    },
    true
  );

  /* ------------------------------------------------------------ FAQ opens */

  // `toggle` does not bubble, so it can only be heard on an ancestor during the
  // capture phase. Collapsing is not interest, so only the open is reported.
  document.addEventListener(
    'toggle',
    function (event) {
      var details = event.target;
      if (!details || details.tagName !== 'DETAILS' || !details.open) return;

      var summary = details.querySelector('summary');
      push('expert_faq_open', {
        cta_location: zoneOf(details),
        question: summary ? (summary.textContent || '').replace(/\s+/g, ' ').trim().slice(0, 120) : ''
      });
    },
    true
  );

  /* --------------------------------------------------------- roster view */

  // Seeing the team is the moment the page's central claim is either believed
  // or not, so it is worth a signal of its own. Guarded: an old browser
  // without IntersectionObserver simply does not report it.
  var roster = document.querySelector('.bd-experts');
  if (roster && typeof window.IntersectionObserver === 'function') {
    var observer = new window.IntersectionObserver(
      function (entries) {
        for (var i = 0; i < entries.length; i++) {
          if (!entries[i].isIntersecting) continue;
          observer.disconnect();
          push('expert_roster_view', {
            experts_shown: roster.querySelectorAll('.bd-expert').length
          });
          return;
        }
      },
      { threshold: 0.25 }
    );
    observer.observe(roster);
  }

  /* ----------------------------------------------------------------- view */

  push('expert_page_view', {});
})();
